import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Radio, SwitchCamera, Mic, MicOff, Square, ArrowLeft, Lock, Globe, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { getPost, updatePost } from "@/lib/stage";
import { useLiveRoom } from "@/lib/useLiveRoom";
import { LiveVideo, LiveChat } from "@/components/live/LiveParts";

/** Host console: camera preview → Go live → stream + chat + viewer count → End (recording uploads progressively). */
export default function LiveHost() {
  const { postId } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [elig, setElig] = useState(null);
  const [armed, setArmed] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [upload, setUpload] = useState(null); // { received, done }
  const [afterShow, setAfterShow] = useState(false);
  const live = useLiveRoom({ postId, role: "host", enabled: armed });
  const rec = useRef({ recorder: null, uploadId: null, index: 0, queue: Promise.resolve(), bytes: 0 });

  useEffect(() => {
    getPost(postId).then(setPost).catch(() => nav("/seller/stage"));
    api.get("/live/eligibility").then(({ data }) => setElig(data)).catch(() => {});
  }, [postId, nav]);

  // Start host-side recording once the local stream exists
  useEffect(() => {
    if (live.state !== "live" || !live.localStream || rec.current.recorder) return;
    (async () => {
      try {
        const mime = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"].find((m) => window.MediaRecorder?.isTypeSupported(m));
        if (!mime) return;
        const { data } = await api.post(`/live/${postId}/recording/start`, { content_type: mime });
        const r = new MediaRecorder(live.localStream, { mimeType: mime, videoBitsPerSecond: 1_800_000 });
        rec.current = { recorder: r, uploadId: data.upload_id, index: 0, queue: Promise.resolve(), bytes: 0 };
        r.ondataavailable = (e) => {
          if (!e.data || e.data.size === 0) return;
          const i = rec.current.index++;
          rec.current.queue = rec.current.queue.then(() => api.put(`/live/recording/${rec.current.uploadId}/chunk/${i}`, e.data, { headers: { "Content-Type": "application/octet-stream" } })
            .then(({ data: d }) => { rec.current.bytes = d.received; setUpload({ received: d.received, done: false }); }).catch(() => {}));
        };
        r.start(8000);
      } catch { /* recording is best-effort */ }
    })();
  }, [live.state, live.localStream, postId]);

  const endShow = async () => {
    const r = rec.current;
    try {
      if (r.recorder && r.recorder.state !== "inactive") {
        await new Promise((res) => { r.recorder.onstop = res; r.recorder.stop(); });
        await r.queue;
        setUpload((u) => ({ ...(u || { received: 0 }), finishing: true }));
        await api.post(`/live/recording/${r.uploadId}/finish`);
        setUpload((u) => ({ ...(u || { received: 0 }), done: true }));
      }
      await api.post(`/live/${postId}/end`);
      live.disconnect();
      toast.success(r.recorder ? "Show ended — the recording is saved" : "Show ended");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Show ended, but the recording could not be saved");
      await api.post(`/live/${postId}/end`).catch(() => {});
      live.disconnect();
    }
    setAfterShow(true);
  };
  const finish = async (visibility) => {
    try { await updatePost(postId, { visibility }); } catch { /* keep default */ }
    toast.success(visibility === "private" ? "Replay kept private — only you can open it" : "Replay is public on your Stage");
    nav(`/store/${post.seller_id}?post=${postId}`);
  };

  if (!post) return <div className="min-h-[60vh] flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-white/20 border-t-white animate-spin" /></div>;
  const blocked = elig && !elig.can_go_live && !post.live_started_at;
  const noEngine = elig && !elig.engine_ready;

  return (
    <div className="h-[100dvh] overflow-hidden bg-black text-white flex flex-col" data-testid="live-host">
      {afterShow && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" data-testid="live-after-show">
          <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0F0F10] p-6 text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9F0A] font-bold">Show ended</div>
            <h2 className="mt-2 text-xl font-black">Who should see the replay?</h2>
            <p className="mt-1 text-sm text-neutral-400">You can change this any time from your Stage Studio.</p>
            <div className="mt-5 grid gap-2">
              <Button onClick={() => finish("public")} data-testid="live-after-public" className="h-12 rounded-2xl bg-white text-black hover:bg-neutral-200 font-bold justify-start px-4"><Globe className="h-5 w-5 mr-3" /><span className="text-left"><span className="block">Public on my Stage</span><span className="block text-[11px] font-normal text-neutral-600">Anyone who signs in can watch and shop</span></span></Button>
              <Button onClick={() => finish("private")} variant="outline" data-testid="live-after-private" className="h-12 rounded-2xl border-white/20 bg-transparent text-white hover:bg-white/5 hover:text-white font-bold justify-start px-4"><EyeOff className="h-5 w-5 mr-3" /><span className="text-left"><span className="block">Private — only me</span><span className="block text-[11px] font-normal text-neutral-500">Hidden from your Stage, the feed and sharing</span></span></Button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
        <Link to="/seller/stage" className="text-neutral-400 hover:text-white" data-testid="live-host-back"><ArrowLeft className="h-5 w-5" /></Link>
        <div className="min-w-0 flex-1"><div className="text-[10px] uppercase tracking-widest text-[#FF3B30] font-bold">{live.state === "live" ? "● Live on VISMARKS" : "VISMARKS Live"}</div><div className="font-bold truncate" data-testid="live-host-title">{post.title}</div></div>
        {live.state === "live" && <div className="text-xs text-neutral-300" data-testid="live-host-viewers">{live.viewers} watching</div>}
      </div>

      <div className="flex-1 grid grid-rows-[1fr_220px] md:grid-rows-1 md:grid-cols-[1fr_320px] min-h-0">
        <div className="relative bg-black flex items-center justify-center min-h-0 overflow-hidden">
          {live.state === "live" && <LiveVideo videoEl={live.videoEl} muted />}
          {live.state === "connecting" && <div className="text-sm text-neutral-400">Connecting your camera…</div>}
          {live.state === "error" && <div className="p-6 text-center max-w-sm"><div className="text-[#FF3B30] font-bold">Couldn't go live</div><div className="mt-1 text-sm text-neutral-400" data-testid="live-host-error">{live.error}</div><Button onClick={() => setArmed(false)} className="mt-4 rounded-full bg-white text-black">Back</Button></div>}
          {live.state === "idle" && !armed && (
            <div className="p-6 text-center max-w-md" data-testid="live-host-ready">
              <div className="mx-auto h-16 w-16 rounded-full bg-[#FF3B30]/15 flex items-center justify-center"><Radio className="h-8 w-8 text-[#FF3B30]" /></div>
              <h2 className="mt-4 text-xl font-black">Ready when you are</h2>
              <p className="mt-1 text-sm text-neutral-400">Your phone or camera streams straight to your audience inside VISMARKS. Everyone in your audience gets a notification the moment you start. The show is recorded and saved to your Stage automatically.</p>
              {elig?.first_show_free && <div className="mt-3 text-xs text-[#34C759] font-semibold" data-testid="live-first-free">Your first live show is on us.</div>}
              {blocked && <div className="mt-3 rounded-xl border border-[#FF9F0A]/40 bg-[#FF9F0A]/10 p-3 text-sm text-[#FF9F0A] flex gap-2" data-testid="live-host-blocked"><Lock className="h-4 w-4 mt-0.5 shrink-0" /><span>{elig.reason} <Link to="/billing" className="underline font-semibold">See plans</Link></span></div>}
              {noEngine && <div className="mt-3 rounded-xl border border-white/15 p-3 text-xs text-neutral-400" data-testid="live-no-engine">VISMARKS Live is being switched on — streaming opens here shortly.</div>}
              <Button disabled={blocked || noEngine} onClick={() => setArmed(true)} data-testid="live-go-live-btn" className="mt-5 h-12 px-8 rounded-full bg-[#FF3B30] text-white hover:bg-[#e0352b] font-black text-base"><Radio className="h-5 w-5 mr-2" />Go live now</Button>
            </div>
          )}
          {live.state === "live" && (
            <div className="absolute bottom-4 inset-x-0 flex items-center justify-center gap-3">
              <Button size="icon" variant="outline" onClick={live.flipCamera} data-testid="live-flip-btn" className="rounded-full h-11 w-11 bg-black/50 border-white/30 text-white hover:bg-black/70"><SwitchCamera className="h-5 w-5" /></Button>
              <Button size="icon" variant="outline" onClick={() => { live.toggleMic(); setMicOn((v) => !v); }} data-testid="live-mic-btn" className="rounded-full h-11 w-11 bg-black/50 border-white/30 text-white hover:bg-black/70">{micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5 text-[#FF3B30]" />}</Button>
              <Button onClick={endShow} data-testid="live-end-btn" className="rounded-full h-11 px-5 bg-white text-black hover:bg-neutral-200 font-bold"><Square className="h-4 w-4 mr-2 fill-current" />End show</Button>
            </div>
          )}
          {upload && !upload.done && live.state !== "live" && <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center text-sm" data-testid="live-upload-progress"><div className="h-8 w-8 rounded-full border-2 border-white/20 border-t-white animate-spin" /><div className="mt-3">Saving your recording… {(upload.received / 1048576).toFixed(1)} MB</div></div>}
        </div>
        <div className="border-t md:border-t-0 md:border-l border-white/10 min-h-0">
          <LiveChat messages={live.messages} onSend={live.send} viewers={live.viewers} disabled={live.state !== "live"} placeholder="Talk to your audience…" />
        </div>
      </div>
    </div>
  );
}
